import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { otpSerializer } from './otpSerializer';

// validation helpers
function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone) {
  if (!phone) return false;
  const phoneDigits = phone.replace(/\D/g, '');
  return phoneDigits.length >= 8 && phoneDigits.length <= 15;
}

// store pour l'état OTP
const otpStore = {
  currentRequestId: null,
  email: null,
  phoneNumber: null,
  prefix: null,
  channel: null, // 'email' ou 'whatsapp'
  otpData: null,
  step: 'input' // 'input' ou 'verify'
};

// envoyer OTP
async function sendOtp(data) {
  const reqData = otpSerializer.serialize({
    email: data.email,
    prefix: data.prefix,
    phoneNumber: data.phoneNumber,
    channel: data.channel,
    name: data.name
  });
  const requestId = `otp-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.OTP.SEND}${requestId}`,
    method: 'POST',
    data: reqData
  });

  return new Promise((resolve) => {
    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        const parsed = otpSerializer.deserialize(res);
        resolve(parsed);
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  });
}

// vérifier OTP
function verifyOtp(inputOtp, storedOtpData) {
  if (!storedOtpData) {
    return { success: false, message: 'OTP expiré' };
  }

  // convertir exp en millisecondes si c'est en secondes (timestamp Unix < 10000000000 signifie secondes)
  const expMs = storedOtpData.exp < 10000000000
    ? storedOtpData.exp * 1000
    : storedOtpData.exp;

  if (expMs < Date.now()) {
    return { success: false, message: 'OTP expiré' };
  }

  if (inputOtp != storedOtpData.otp) {
    return { success: false, message: 'OTP incorrect' };
  }
  return { success: true };
}

// listener pour l'événement SEND_OTP
bus.on(constants.EVENTS.OTP.SEND_OTP, async (config) => {
  const {
    requestId,
    channel = 'email', // 'email' ou 'whatsapp'
    showContactInput = true, // afficher l'input pour demander email/contact
    validate = true, // si true, demande aussi de valider l'OTP
    email = null,
    phoneNumber = null,
    prefix = null,
    name = null
  } = config;

  otpStore.currentRequestId = requestId;
  otpStore.channel = channel;
  otpStore.validate = validate;

  // si on a déjà les infos, on envoie direct
  if (!showContactInput && (email || phoneNumber)) {
    otpStore.email = email;
    otpStore.phoneNumber = phoneNumber;
    otpStore.prefix = prefix;
    otpStore.step = 'verify';

    bus.emit(constants.EVENTS.LOADING.START);

    const otpRes = await sendOtp({
      email,
      phoneNumber,
      prefix,
      channel,
      name
    });

    bus.emit(constants.EVENTS.LOADING.FINISH);

    if (!otpRes.success) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'OTP non envoyé',
        body: otpRes.message || 'Erreur lors de l\'envoi de l\'OTP'
      });
      bus.emit(constants.EVENTS.USER.OTP_ERROR, {
        requestId,
        error: otpRes.message
      });
      return;
    }

    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Code envoyé',
      body: 'Un code de vérification a été envoyé à votre email'
    });

    otpStore.otpData = otpRes.data;

    // si on ne doit pas valider, on émet success direct
    if (!validate) {
      bus.emit(constants.EVENTS.USER.OTP_SENT, {
        requestId,
        otpData: otpRes.data
      });
      return;
    }

    // sinon on affiche l'interface de validation
    bus.emit(constants.EVENTS.OTP.SHOW_OTP_VERIFICATION);
    return;
  }

  // sinon on affiche l'interface pour demander email/contact
  otpStore.step = 'input';
  bus.emit(constants.EVENTS.OTP.SHOW_OTP_INPUT, {
    channel,
    requestId
  });
});

// listener pour soumettre le contact (email ou téléphone)
bus.on(constants.EVENTS.OTP.OTP_SUBMIT_CONTACT, async (data) => {
  const { email, phoneNumber, prefix } = data;

  // validation email
  if (otpStore.channel === 'email') {
    if (!isValidEmail(email)) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Email invalide',
        body: 'Veuillez entrer une adresse email valide'
      });
      return;
    }
  }

  // validation téléphone
  if (otpStore.channel === 'whatsapp') {
    if (!phoneNumber) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Numéro requis',
        body: 'Veuillez entrer votre numéro WhatsApp'
      });
      return;
    }
    if (!isValidPhone(phoneNumber)) {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        title: 'Numéro invalide',
        body: 'Veuillez entrer un numéro de téléphone valide'
      });
      return;
    }
  }

  otpStore.email = email;
  otpStore.phoneNumber = phoneNumber;
  otpStore.prefix = prefix;

  bus.emit(constants.EVENTS.LOADING.START);

  const otpRes = await sendOtp({
    email,
    phoneNumber,
    prefix,
    channel: otpStore.channel
  });

  bus.emit(constants.EVENTS.LOADING.FINISH);

  if (!otpRes.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'OTP non envoyé',
      body: otpRes.message || 'Erreur lors de l\'envoi de l\'OTP'
    });
    return;
  }

  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'Code envoyé',
    body: 'Un code de vérification a été envoyé à votre email'
  });

  otpStore.otpData = otpRes.data;

  // si on ne doit pas valider, on émet success direct
  if (!otpStore.validate) {
    bus.emit(constants.EVENTS.OTP.OTP_SENT, {
      requestId: otpStore.currentRequestId,
      otpData: otpRes.data
    });
    return;
  }

  // sinon on affiche l'interface de validation
  otpStore.step = 'verify';
  bus.emit(constants.EVENTS.OTP.SHOW_OTP_VERIFICATION);
});

// listener pour valider l'OTP
bus.on(constants.EVENTS.OTP.OTP_VERIFY, (inputOtp) => {
  // validation
  if (!inputOtp || inputOtp.length !== 5) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Code incomplet',
      body: 'Veuillez entrer les 5 chiffres du code'
    });
    return;
  }

  const result = verifyOtp(inputOtp, otpStore.otpData);

  if (!result.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'OTP invalide',
      body: result.message
    });
    bus.emit(constants.EVENTS.OTP.OTP_ERROR, {
      requestId: otpStore.currentRequestId,
      error: result.message
    });
    return;
  }

  // OTP validé
  bus.emit(constants.EVENTS.OTP.OTP_VALIDATED, {
    requestId: otpStore.currentRequestId,
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix,
    channel: otpStore.channel,
    otpData: otpStore.otpData
  });

  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'OTP validé',
    body: 'Votre code a été vérifié avec succès'
  });

  // reset le store
  otpStore.currentRequestId = null;
  otpStore.email = null;
  otpStore.phoneNumber = null;
  otpStore.prefix = null;
  otpStore.otpData = null;
  otpStore.step = 'input';
});

// listener pour réenvoyer l'OTP
bus.on(constants.EVENTS.OTP.OTP_RESEND, async () => {
  if (!otpStore.email && !otpStore.phoneNumber) return;

  bus.emit(constants.EVENTS.LOADING.START);

  const otpRes = await sendOtp({
    email: otpStore.email,
    phoneNumber: otpStore.phoneNumber,
    prefix: otpStore.prefix,
    channel: otpStore.channel
  });

  bus.emit(constants.EVENTS.LOADING.FINISH);

  if (!otpRes.success) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'OTP non envoyé',
      body: otpRes.message || 'Erreur lors de l\'envoi de l\'OTP'
    });
    return;
  }

  otpStore.otpData = otpRes.data;
  bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
    title: 'OTP renvoyé',
    body: 'Un nouveau code vous a été envoyé'
  });
});

// listener pour annuler
bus.on(constants.EVENTS.OTP.OTP_CANCEL, () => {
  // reset le store
  otpStore.currentRequestId = null;
  otpStore.email = null;
  otpStore.phoneNumber = null;
  otpStore.prefix = null;
  otpStore.otpData = null;
  otpStore.step = 'input';
});

export { otpStore };
